<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DxTabPanel, { DxItem as DxTabItem } from 'devextreme-vue/tab-panel';
import { DxButton } from 'devextreme-vue/button';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxTagBox } from 'devextreme-vue/tag-box';
import {
  BottoPagePanel,
  LookupBox,
  getSenderNamesLookup,
  showError,
  showSuccess,
} from '@botto/core';
import { createProject } from '@botto/module-cdp/api';
import { TransitionsEditor } from '@botto/module-cdp/transitions';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { getDirectionsLookup } from '../../lookups';
import { getCdpProjectsLookup } from '../../lookups/cdpProjects';
import { getSmppClientsLookup } from '../../api/adapters/accounts';
import { createGroupsAdapter } from '../../api';
import type { StreamGroup } from '../../api';
import {
  CDP_POSTBACK_EVENTS,
  createDefaultCdpPostbackConfig,
  parseCdpPostbackConfig,
  pairsFromRecord,
  recordFromPairs,
  serializeCdpPostbackConfig,
  type CdpFieldPair,
  type CdpPostbackConfig,
} from './cdpPostbackConfig';

const route = useRoute();
const router = useRouter();

const streamId = computed(() => {
  const n = Number(route.params.streamId);
  return Number.isFinite(n) ? n : null;
});

const isNew = computed(() => route.params.groupId == null || route.path.endsWith('/new'));
const groupId = computed(() => {
  if (isNew.value) return null;
  const n = Number(route.params.groupId);
  return Number.isFinite(n) ? n : null;
});

const loading = ref(false);
const saving = ref(false);

const form = ref({
  stream_id: null as number | null,
  direction_id: null as number | null,
  name: null as string | null,
  link_handler_key: null as string | null,
});

const cdp = ref<CdpPostbackConfig>(createDefaultCdpPostbackConfig());
const fieldMappingPairs = ref<CdpFieldPair[]>(pairsFromRecord(cdp.value.field_mapping));
const extraStaticPairs = ref<CdpFieldPair[]>([]);

const methodItems = ['POST', 'GET'];

const smppClientsLookup = getSmppClientsLookup();
const directionsLookup = getDirectionsLookup();
const cdpProjectsLookup = getCdpProjectsLookup();
const senderNamesLookup = getSenderNamesLookup();

const creatingProject = ref(false);
const projectPopupOpen = ref(false);
const projectForm = ref({
  name: '',
  system_id: '',
  sender_name: '',
});

const pageTitle = computed(() =>
  isNew.value ? 'Новая группа маршрутизации' : `Группа #${groupId.value}`,
);

const projectId = computed({
  get: () => cdp.value.static_fields.project_id || null,
  set: (v: string | null) => {
    cdp.value.static_fields = {
      ...cdp.value.static_fields,
      project_id: v ?? '',
    };
  },
});

const senderNameItems = computed(() => {
  const seen = new Set<string>();
  const items: { value: string; text: string }[] = [];
  for (const row of senderNamesLookup.items.value) {
    const value = (row.name || row.value || '').trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    items.push({ value, text: value });
  }
  const current = projectForm.value.sender_name.trim();
  if (current && !seen.has(current)) items.unshift({ value: current, text: current });
  return items;
});

function streamSystemId(): string {
  const sid = form.value.stream_id ?? streamId.value;
  if (sid == null) return '';
  const client = smppClientsLookup.byId.value.get(sid);
  return (client?.system_id || '').trim();
}

function openCreateProject() {
  projectForm.value = {
    name: form.value.name?.trim() || '',
    system_id: streamSystemId(),
    sender_name: '',
  };
  projectPopupOpen.value = true;
}

async function saveNewProject() {
  const name = projectForm.value.name.trim();
  const system_id = projectForm.value.system_id.trim();
  const sender_name = projectForm.value.sender_name.trim();
  if (!name || !system_id || !sender_name) {
    showError('Укажите название, system_id и sender_name');
    return;
  }
  creatingProject.value = true;
  try {
    const created = await createProject({ name, system_id, sender_name });
    await cdpProjectsLookup.reload();
    projectId.value = created.id;
    projectPopupOpen.value = false;
    showSuccess('Проект CDP создан');
  } catch (e) {
    showError(e);
  } finally {
    creatingProject.value = false;
  }
}

function applyCdpConfig(raw: Record<string, unknown> | null | undefined) {
  const parsed = parseCdpPostbackConfig(raw);
  cdp.value = parsed;
  fieldMappingPairs.value = pairsFromRecord(parsed.field_mapping);

  const { project_id: _projectId, ...restStatic } = parsed.static_fields;
  extraStaticPairs.value = Object.keys(restStatic).length
    ? pairsFromRecord(restStatic)
    : [];
}

function applyGroup(data: StreamGroup) {
  form.value = {
    stream_id: data.stream_id ?? streamId.value,
    direction_id: data.direction_id ?? null,
    name: data.name ?? null,
    link_handler_key: data.link_handler_key ?? null,
  };
  applyCdpConfig(data.cdp_postback_config as Record<string, unknown> | null);
}

function buildCdpPayload(): Record<string, unknown> {
  const fieldMapping = recordFromPairs(fieldMappingPairs.value);
  const extraStatic = recordFromPairs(extraStaticPairs.value);
  const config: CdpPostbackConfig = {
    ...cdp.value,
    field_mapping: Object.keys(fieldMapping).length ? fieldMapping : { ...cdp.value.field_mapping },
    static_fields: {
      ...extraStatic,
      project_id: cdp.value.static_fields.project_id ?? '',
    },
  };
  return serializeCdpPostbackConfig(config);
}

function resetForm() {
  form.value = {
    stream_id: streamId.value,
    direction_id: null,
    name: null,
    link_handler_key: null,
  };
  applyCdpConfig(null);
}

async function loadGroup() {
  if (streamId.value == null) {
    showError('Не указан канал (stream_id)');
    return;
  }
  if (isNew.value || groupId.value == null) {
    resetForm();
    return;
  }
  loading.value = true;
  try {
    const adapter = createGroupsAdapter(streamId.value);
    const data = await adapter.byKey?.(groupId.value);
    if (data) applyGroup(data);
  } catch (e) {
    showError(e);
  } finally {
    loading.value = false;
  }
}

async function save() {
  const sid = form.value.stream_id ?? streamId.value;
  if (!sid || !form.value.direction_id) {
    showError('Выберите канал и направление');
    return;
  }
  saving.value = true;
  try {
    const adapter = createGroupsAdapter(sid);
    const cdpConfig = buildCdpPayload();

    if (isNew.value) {
      const created = await adapter.create!({
        stream_id: sid,
        direction_id: form.value.direction_id,
        name: form.value.name,
        link_handler_key: form.value.link_handler_key,
        cdp_postback_config: cdpConfig,
      });
      showSuccess('Группа создана');
      await router.replace(`/sms/routing-choices/${sid}/${created.id}`);
    } else {
      await adapter.update!({
        id: groupId.value!,
        stream_id: sid,
        direction_id: form.value.direction_id,
        name: form.value.name,
        link_handler_key: form.value.link_handler_key,
        cdp_postback_config: cdpConfig,
      });
      showSuccess('Группа сохранена');
      await loadGroup();
    }
  } catch (e) {
    showError(e);
  } finally {
    saving.value = false;
  }
}

function goBack() {
  const sid = form.value.stream_id ?? streamId.value;
  void router.push({
    path: '/sms/routing-choices',
    query: sid != null ? { streamId: String(sid) } : undefined,
  });
}

function openTraffic() {
  const sid = form.value.stream_id ?? streamId.value;
  if (sid == null || groupId.value == null) return;
  void router.push(`/sms/routing-choices/${sid}/${groupId.value}/routing`);
}

function addFieldMappingRow() {
  fieldMappingPairs.value = [...fieldMappingPairs.value, { key: '', value: '' }];
}

function removeFieldMappingRow(index: number) {
  fieldMappingPairs.value = fieldMappingPairs.value.filter((_, i) => i !== index);
  if (!fieldMappingPairs.value.length) {
    fieldMappingPairs.value = [{ key: '', value: '' }];
  }
}

function addStaticFieldRow() {
  extraStaticPairs.value = [...extraStaticPairs.value, { key: '', value: '' }];
}

function removeStaticFieldRow(index: number) {
  extraStaticPairs.value = extraStaticPairs.value.filter((_, i) => i !== index);
}

watch(
  () => [route.params.streamId, route.params.groupId, route.path],
  () => {
    void loadGroup();
  },
  { immediate: true },
);

onMounted(async () => {
  await Promise.all([
    smppClientsLookup.ensureLoaded(),
    directionsLookup.ensureLoaded(),
    cdpProjectsLookup.ensureLoaded(),
    senderNamesLookup.ensureLoaded(),
  ]);
});
</script>

<template>
  <BottoPagePanel :title="pageTitle" standalone>
    <template #actions>
      <DxButton text="Назад" styling-mode="outlined" @click="goBack" />
      <DxButton
        v-if="!isNew"
        text="Распределение трафика"
        icon="hierarchy"
        styling-mode="outlined"
        @click="openTraffic"
      />
      <DxButton
        text="Сохранить"
        type="default"
        styling-mode="contained"
        :disabled="saving || loading"
        @click="save"
      />
    </template>

    <DxTabPanel
      class="rc-tabs"
      height="auto"
      :animation-enabled="true"
      :swipe-enabled="false"
      :defer-rendering="false"
    >
      <DxTabItem title="Основное">
        <div class="rc-form">
          <div class="rc-field">
            <label>Канал (клиент)</label>
            <LookupBox
              v-model="form.stream_id"
              :lookup="smppClientsLookup"
              placeholder="Выберите канал"
              :show-clear-button="false"
              :disabled="!isNew"
            />
          </div>
          <div class="rc-field">
            <label>Направление</label>
            <LookupBox
              v-model="form.direction_id"
              :lookup="directionsLookup"
              placeholder="Выберите направление"
              :show-clear-button="false"
            />
          </div>
          <div class="rc-field">
            <label>Название</label>
            <DxTextBox
              :value="form.name ?? ''"
              @value-changed="(e: any) => (form.name = e.value || null)"
            />
          </div>
        </div>
      </DxTabItem>

      <DxTabItem title="CDP">
        <div class="rc-cdp">
          <div class="rc-cdp__main">
          <div class="rc-field">
            <DxCheckBox
              :value="cdp.enabled"
              text="Отправка событий включена"
              @value-changed="(e: any) => (cdp.enabled = Boolean(e.value))"
            />
          </div>

          <div class="rc-field">
            <label>Проект CDP</label>
            <div class="rc-project-row">
              <LookupBox
                v-model="projectId"
                :lookup="cdpProjectsLookup"
                placeholder="Проект CDP"
              />
              <DxButton text="Новый проект" styling-mode="outlined" @click="openCreateProject" />
            </div>
            <p class="rc-hint">Задаёт static_fields.project_id. Transitions сохраняются в CDP, не в группу SMS.</p>
          </div>

          <div class="rc-field">
            <label>Link handler key</label>
            <DxTextBox
              :value="form.link_handler_key ?? ''"
              @value-changed="(e: any) => (form.link_handler_key = e.value || null)"
            />
          </div>

          <div class="rc-field">
            <label>CDP URL</label>
            <DxTextBox
              :value="cdp.url"
              placeholder="https://…/api/postbacks"
              @value-changed="(e: any) => (cdp.url = e.value ?? '')"
            />
          </div>

          <div class="rc-row">
            <div class="rc-field">
              <label>Method</label>
              <DxSelectBox
                :value="cdp.method"
                :items="methodItems"
                @value-changed="(e: any) => (cdp.method = e.value === 'GET' ? 'GET' : 'POST')"
              />
            </div>
            <div class="rc-field">
              <label>Timeout, сек</label>
              <DxNumberBox
                :value="cdp.timeout_sec"
                :min="0"
                @value-changed="(e: any) => (cdp.timeout_sec = Number(e.value) || 0)"
              />
            </div>
            <div class="rc-field">
              <label>Max retries</label>
              <DxNumberBox
                :value="cdp.max_retries"
                :min="0"
                @value-changed="(e: any) => (cdp.max_retries = Number(e.value) || 0)"
              />
            </div>
            <div class="rc-field">
              <label>Retry delay, сек</label>
              <DxNumberBox
                :value="cdp.retry_delay_sec"
                :min="0"
                @value-changed="(e: any) => (cdp.retry_delay_sec = Number(e.value) || 0)"
              />
            </div>
          </div>

          <div class="rc-field">
            <label>Events</label>
            <DxTagBox
              :value="cdp.events"
              :items="[...CDP_POSTBACK_EVENTS]"
              :show-selection-controls="true"
              apply-value-mode="useButtons"
              :search-enabled="true"
              @value-changed="(e: any) => (cdp.events = e.value ?? [])"
            />
          </div>

          <div class="rc-section">
            <div class="rc-section__head">
              <h3>Field mapping</h3>
              <DxButton text="Добавить" icon="plus" styling-mode="outlined" @click="addFieldMappingRow" />
            </div>
            <div
              v-for="(pair, index) in fieldMappingPairs"
              :key="`fm-${index}`"
              class="rc-pair"
            >
              <DxTextBox
                :value="pair.key"
                placeholder="поле CDP"
                @value-changed="(e: any) => (pair.key = e.value ?? '')"
              />
              <DxTextBox
                :value="pair.value"
                placeholder="поле SMS"
                @value-changed="(e: any) => (pair.value = e.value ?? '')"
              />
              <DxButton
                icon="trash"
                styling-mode="text"
                hint="Удалить"
                @click="removeFieldMappingRow(index)"
              />
            </div>
          </div>

          <div class="rc-section">
            <div class="rc-section__head">
              <h3>Static fields</h3>
              <DxButton text="Добавить" icon="plus" styling-mode="outlined" @click="addStaticFieldRow" />
            </div>
            <div
              v-for="(pair, index) in extraStaticPairs"
              :key="`sf-${index}`"
              class="rc-pair"
            >
              <DxTextBox
                :value="pair.key"
                placeholder="ключ"
                @value-changed="(e: any) => (pair.key = e.value ?? '')"
              />
              <DxTextBox
                :value="pair.value"
                placeholder="значение"
                @value-changed="(e: any) => (pair.value = e.value ?? '')"
              />
              <DxButton
                icon="trash"
                styling-mode="text"
                hint="Удалить"
                @click="removeStaticFieldRow(index)"
              />
            </div>
          </div>
          </div>

          <div class="rc-cdp__transitions">
            <TransitionsEditor :project-id="projectId" />
          </div>
        </div>
      </DxTabItem>
    </DxTabPanel>

    <DxPopup
      v-model:visible="projectPopupOpen"
      title="Новый проект CDP"
      :width="480"
      :height="380"
    >
      <template #content>
        <div class="tf">
          <div class="tf__field">
            <label>Название</label>
            <DxTextBox
              :value="projectForm.name"
              @value-changed="(e: any) => (projectForm.name = e.value ?? '')"
            />
          </div>
          <div class="tf__field">
            <label>System ID</label>
            <DxTextBox
              :value="projectForm.system_id"
              @value-changed="(e: any) => (projectForm.system_id = e.value ?? '')"
            />
          </div>
          <div class="tf__field">
            <label>Sender name</label>
            <DxSelectBox
              :value="projectForm.sender_name"
              :items="senderNameItems"
              value-expr="value"
              display-expr="text"
              :search-enabled="true"
              :accept-custom-value="true"
              placeholder="Имя отправителя"
              @customItemCreating="(e: any) => { const v = String(e.text ?? '').trim(); e.customItem = v ? { value: v, text: v } : null; }"
              @value-changed="(e: any) => (projectForm.sender_name = String(e.value ?? ''))"
            />
          </div>
        </div>
      </template>
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Создать', type: 'default', disabled: creatingProject, onClick: saveNewProject }"
      />
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Отмена', onClick: () => (projectPopupOpen = false) }"
      />
    </DxPopup>
  </BottoPagePanel>
</template>

<style scoped>
.rc-tabs :deep(.dx-tabpanel-container),
.rc-tabs :deep(.dx-multiview-wrapper),
.rc-tabs :deep(.dx-multiview-item),
.rc-tabs :deep(.dx-multiview-item-content) {
  height: auto !important;
}

.rc-tabs :deep(.dx-multiview-item-content) {
  overflow: visible;
}

.rc-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
  padding: 8px 0 32px;
}

.rc-cdp {
  display: grid;
  grid-template-columns: minmax(320px, 520px) minmax(0, 1fr);
  gap: 24px 32px;
  align-items: start;
  padding: 8px 0 32px;
}

.rc-cdp__main,
.rc-cdp__transitions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

@media (max-width: 1100px) {
  .rc-cdp {
    grid-template-columns: 1fr;
  }
}

.rc-project-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.rc-project-row > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.tf {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tf__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tf__field label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}

.rc-field label,
.rc-section h3 {
  display: block;
  margin: 0 0 4px;
  font-weight: 500;
}

.rc-field .rc-hint {
  margin-top: 6px;
}

.rc-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.rc-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rc-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.rc-pair {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 8px;
  align-items: center;
}

.rc-hint {
  margin: 0;
  font-size: 12px;
  color: var(--dx-color-text-secondary, #6b7280);
}
</style>
